/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import React, { useState} from "react";  // Import React and hooks for component functionality
import { useDispatch, useSelector } from "react-redux";  // Import Redux hooks for dispatching actions and accessing state
import {getStatusNameById, status, STATUS_MODEL} from "../../../../enums/statusEnum";  // Import status enum for event statuses
import {DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat, getFullName} from "../../../../Apis/functions";  // Import utility function for date formatting
import routes from "../../../../Routes/routes";  // Import routes for navigation
import { useNavigate } from "react-router-dom";  // Import hook for navigation
import { TABLES } from "../../../../Apis/Tables";  // Import table constants
import CustomTh from "../../../../components/table/CustomTh";  // Import custom table header component
import CustomTd from "../../../../components/table/CustomTd";  // Import custom table cell component
import { SEARCH_MODEL} from "../../../../components/table/Model/TableModel";  // Import search and criteria models
import { SimpleSearch } from "../../../../reduxToolkit/Search/SearchApi";  // Import Redux API functions for search
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem";  // Import dropdown menu item component
import TableFunction from "../../../../components/table/TableFunction";  // Import table function utility
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile";
import CustomChip from "../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum";
import {
    CancelIconsOptions,
    DeleteIconsOptions, DetailsIconsOptions, IconClockTable,
    IconMarkerTable,
    IconScheduleTable,
    NextIconsOptions
} from "../../../../components/buttons/IconButtons";
import {permissionObj} from "../../../../Helpers/permission";
import {EVENT_STATUS, RESERVATIONS_SUB_VIEW_MODEL, TABS_RESERVATIONS} from "../Model/Model";
import {ACTION_MODEL} from '../../../../Helpers/ActionModel';
import {invitationInTime} from "../../Visitor/Invitation/Model/Model";

// EventTableApp component definition
const EventTableApp = ({
    selectedList,
    setSelectedList,
    setBodyForExport,
    modified,
    setModified,
    searchElement,
    handleShowModals,
    setResults,
    results,
    option,
    setIsAllChecked
}) => {
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [tableId, setTableId] = useState("tbl_eta_reservations");
    const [searchId, setSearchId] = useState("txt_eta_reservations");
    const showCheckbox = (DEV || permission?.includes(permissionObj?.WEB_EVENT_DELETE) || permission?.includes(permissionObj?.WEB_EVENT_CANCEL));
    const leftPosition = showCheckbox ? 50 : 0;


    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('host')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('access_zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('start_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('end_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('approval_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('booking_status')}</CustomTh>
        ];
    };

    // Define the table body based on the event data
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };  // If no results, return empty body and data

        let body = results?.content?.map((item) => {
            const customStatusId = item?.statusId === STATUS_MODEL.CANCELED ? item?.statusId : !invitationInTime(item?.endDate) ? STATUS_MODEL.OVERDUE : item?.statusId;
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                        {item?.name ? item?.name : EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{getFullName(item.hostName, item.hostLastName, item.hostSecondLastName)}</CustomTd>
                      <CustomTd tableId={tableId}>
                        <div className={"d-flex"}>
                            <div><IconMarkerTable className={"mr-8px mb-1"}/></div>
                           {item.reservationZoneName}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className={"d-flex"}>
                            <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            {getDateFormat(item?.startDate,false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        </div>
                        <div className={"d-flex"}>
                            <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            {item.reservationFromTime}
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <div className={"d-flex"}>
                            <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            {getDateFormat(item?.endDate,false,DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        </div>
                        <div className={"d-flex"}>
                            <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            {item.reservationToTime}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={statusColor[item?.approvalStatusId]}
                            fontColor={statusFontColor[item?.approvalStatusId]}
                            borderColor={statusBorderColor[item?.approvalStatusId]}
                            legend={t(getStatusNameById(item?.approvalStatusId) || '')}
                            variant={'large'}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={statusColor[customStatusId]}
                            fontColor={statusFontColor[customStatusId]}
                            borderColor={statusBorderColor[customStatusId]}
                            legend={t(status[customStatusId])}
                            variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });

        return { body: body, data: results?.content };  // Return the generated body and data
    };

    // Generate actions buttons for each event row
    const getActionsButtons = (item, index) => {
        const inTime = invitationInTime(item?.endDate);

        return (
            <>
                <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
                    {(DEV || (permission?.includes(permissionObj?.WEB_APPROVAL_FLOW_UPDATE) && permission?.includes(permissionObj?.WEB_APPROVAL_FLOW_READ)))
                    && item?.approvalFlowId
                    &&(
                        <DropDownMenuItem
                            id={'btn_eta_approval-tracker' + item.id}
                            index={index}
                            name={t('approval_tracker')}
                            url={
                                {  pathname: routes.approvalTracker.main + TABLES.EVENT + '/' + item?.id + '/' + item?.approvalFlowId,
                                   state: {
                                        backUrl:{
                                            pathname: routes.event.list,
                                            state: {
                                                isOverdue: !inTime
                                            }
                                        }
                                }
                                }
                            }
                            icon={<NextIconsOptions />}
                        />
                    )}

                    <DropDownMenuItem
                        id={'btn_eta_details_' + item.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('details')}
                        click={() => handleViewDetails(item)}
                        icon={<DetailsIconsOptions />}
                    />

                    {EVENT_STATUS.delete.includes(item?.statusId) &&
                        (DEV || permission?.includes(permissionObj?.WEB_EVENT_DELETE)) && (
                            <DropDownMenuItem
                                id={'btn_eta_delete_' + item.id}
                                index={index}
                                handleClose={handleClose}
                                name={t('delete')}
                                click={() => {
                                    handleShowModals(ACTION_MODEL.DELETE, item)
                                }}
                                icon={<DeleteIconsOptions/>}
                                fontColor='font-color-error-600'
                                iconColor={"icon-color-error-code-600"}
                            />
                        )}
                     {EVENT_STATUS.cancel.includes(item?.statusId) &&
                         (DEV || permission?.includes(permissionObj?.WEB_EVENT_CANCEL)) &&
                         inTime && (
                        <DropDownMenuItem
                            id={"btn_eta_cancel_"+item.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('cancel')}
                            click={() =>{
                                handleShowModals(ACTION_MODEL.CANCEL,item)
                            }}
                            icon={<CancelIconsOptions/>}
                            fontColor='font-color-error-600'
                            iconColor={"icon-color-error-code-600"}

                        />)
                    }
                </DropDownMenuProfile>
            </>
        );
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, key: searchElement, model: option });
        setBodyForExport(body);

        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);  // Update the results state if data is successful
                }
            },
        );
    }

    // Table function hook for handling table operations like pagination, selection, etc.
    const { t, thead, tbody, handleCheckboxChange, handleDeleteAll,
        openMenu, handleMenuToggle, handleClose,
        pagination, page, rowsPerPage } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'reservations_per_page',
        setIsAllChecked,
        hasContainer: true, // Enable container for the table
        fixedPagination: false, // Disable fixed pagination
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    const handleViewDetails = (event) => {
        navigate(routes.event.details + `${event?.id}`,{
            state: {
                backUrl: {
                    pathname: routes.event.list,
                    state: {
                        currentTab: TABS_RESERVATIONS.BOOKINGS.INDEX,
                        currentSubView: RESERVATIONS_SUB_VIEW_MODEL.BOOKINGS.REQUEST
                    }
                }
            }
        });
    };

    return {
        tableId,
        searchId,
        thead,
        tbody,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        showCheckbox
    };
}

export default EventTableApp;
